/**
 * A GGUF variant as an `Inventory`.
 *
 * Separate from `inventory.ts` so that `safetensors/inventory.ts` can import
 * this without a cycle: this module depends on the `Inventory` shape, and the
 * per-file reader below it does not.
 *
 * ## Progress is reported in bytes, and only for this format
 *
 * `InventoryProgress` carries `bytesRead` for GGUF and nothing for safetensors.
 * The asymmetry is the honest one — a safetensors shard header is about 2 KB,
 * and a GGUF index sits behind the tokenizer at a measured median of 10.46 MiB.
 * The site's own footer says "metadata analysis, no weights fetched", which
 * stays true of both, but "nearly free" stops being true of one of them.
 *
 * A running total of *real* bytes rather than a forecast, because the forecast
 * was tried and refused: extrapolating the header size from the sampled array
 * came out 66% low, since token strings lengthen through the array and the
 * estimate cannot see keys it has not reached (plan §4). A number that is wrong
 * by two thirds on a page whose entire claim is that its numbers are true is
 * worse than no number.
 */

import type { ResolvedRepo } from "../hf/hub.js";
import { fileProvenanceFromGguf } from "../hf/provenance.js";
import type {
  Inventory,
  InventoryProgress,
  InventoryTensor,
} from "../safetensors/inventory.js";
import { tallyByDtype } from "../safetensors/inventory.js";
import { readGgufFile } from "./inventory.js";
import type { GgufVariant } from "./variants.js";

export async function readGgufInventory(
  repo: ResolvedRepo,
  variant: GgufVariant,
  onProgress?: (progress: InventoryProgress) => void,
): Promise<Inventory> {
  const tensors: InventoryTensor[] = [];
  let bytesRead = 0;
  /*
   * Provenance from the first part, extracted rather than the map kept.
   *
   * A split variant repeats `general.*` in every part — the keys describe the
   * model, not the shard — so the first part that declares anything is enough
   * and merging would do work to reach the same answer.
   *
   * Extracted here rather than kept as a map because `Inventory` is what the
   * IndexedDB cache stores and must have a declared shape. See
   * `Inventory.fileProvenance` for why.
   */
  let fileProvenance: Inventory["fileProvenance"];

  for (const [index, file] of variant.files.entries()) {
    onProgress?.({ done: index, total: variant.files.length, file, bytesRead });
    /*
     * Sequentially, and not because of the concurrency cap. A split variant's
     * parts are read one after another for the same reason sharded safetensors
     * inventories are: every request has to trace back to a visitor action, and
     * fanning out four reads for one click is the shape of prefetching even when
     * the total is identical.
     */
    const part = await readGgufFile(repo, file, (soFar) => {
      // `soFar` is this file's running total, so the report is the completed
      // files plus this one's progress — not a sum that double-counts.
      onProgress?.({
        done: index,
        total: variant.files.length,
        file,
        bytesRead: bytesRead + soFar,
      });
    });
    bytesRead += part.headerBytesRead;
    tensors.push(...part.tensors);
    fileProvenance ??= fileProvenanceFromGguf(part.metadata);
  }

  onProgress?.({
    done: variant.files.length,
    total: variant.files.length,
    file: "",
    bytesRead,
  });

  return {
    repo,
    tensors,
    files: [...variant.files],
    // The variant key, so a deep link reopens this quantization and not another.
    component: variant.key,
    // A split variant is sharded in exactly the sense the rest of the app means:
    // one logical model across several files.
    sharded: variant.files.length > 1,
    ...(fileProvenance ? { fileProvenance } : {}),
    storedElements: tensors.reduce((sum, t) => sum + t.numel, 0),
    totalBytes: tensors.reduce((sum, t) => sum + t.byteLength, 0),
    byDtype: tallyByDtype(tensors),
  };
}
